import "@/styles/components/modal.css";
import delegate from "delegate-it";
import A11yDialog from "a11y-dialog";
import ajax from "@/scripts/common/ajax";
import { fireEvent } from "@/scripts/common/util";

let activeModal;

const generateTemplate = (id, allowOverlayClose = true, title = false, asFull = false, allowButtonClose = true) => {
  const html = `
    <div aria-hidden="true" class="flex overflow-auto fixed inset-0 outline-none z-100 overflow-scroll-touch aria-hidden:hidden" role="dialog" data-modal-wrapper>
      <div class="fixed inset-0 z-0 bg-brand-blue-500/90 backdrop-blur-sm" data-a11y-dialog-hide data-modal-overlay></div>

      <button type="button" class="flex absolute top-0 right-0 p-3 w-16 h-16 text-white transition-opacity hover:opacity-40 modal-close z-2" data-a11y-dialog-hide aria-label="Close dialog">
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path fill="currentColor" d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
        </svg>
      </button>

      <div data-modal-container>
        <svg class="text-white site-loader" viewBox="0 0 50 50" aria-hidden="true">
          <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle>
        </svg>
      </div>
    </div>
  `;

  const template = document.createRange().createContextualFragment(html);

  template.querySelector("[data-modal-wrapper]").id = id;

  if (title) {
    template.querySelector("[data-modal-wrapper]").setAttribute("aria-labeledby", "modal-title");
  }

  if (asFull) {
    template.querySelector("[data-modal-wrapper]").classList.add("as-full");
  }

  if (!allowOverlayClose) {
    template.querySelector("[data-modal-overlay]").removeAttribute("data-a11y-dialog-hide");
  }

  if (!allowButtonClose) {
    template.querySelector("button[data-a11y-dialog-hide]").remove();
  }

  return template;
};

const populateModal = async (opts = {}, modalEl) => {
  const type = opts.type;
  const title = opts.title ?? "";
  const url = opts.url;

  const defaultClose = modalEl.querySelector(".modal-close");

  if (defaultClose) {
    defaultClose.classList.add("invisible");
  }

  let modalContent = "";

  if (type === "video") {
    modalContent = `
      <div class="mx-auto w-full max-w-5xl" role="document" data-modal-content>
        <h1 id="modal-title" class="sr-only">Watch ${title}</h1>
        <div class="relative aspect-video" data-modal-body>
          <iframe title="Watch ${title}" style="border: none" src="${url}?autoplay=1&rel=0&enablejsapi=1" allowfullscreen class="absolute inset-0 w-full h-full"></iframe>
        </div>
      </div>
    `;
  }

  if (type === "content") {
    modalContent = await ajax.get(url).text();
  }

  const content = document.createRange().createContextualFragment(modalContent);

  if (defaultClose) {
    if (content.querySelector("[data-a11y-dialog-hide]")) {
      defaultClose.remove();
    } else {
      defaultClose.classList.remove("invisible");
    }
  }

  modalEl.querySelector("[data-modal-container]").replaceChildren(content);

  fireEvent("modal:populate", document.documentElement, { modalEl });
};

const openModal = (opts = {}) => {
  const type = opts.type;
  const title = opts.title ?? false;
  const asFull = opts.asFull ?? false;
  const allowButtonClose = opts.allowButtonClose ?? true;

  let allowOverlayClose = opts.allowOverlayClose ?? true;

  if (type === "video") {
    allowOverlayClose = false;
  }

  const id = `modal-${Math.floor(Math.random() * Date.now())}`;
  const template = generateTemplate(id, allowOverlayClose, title, asFull, allowButtonClose);

  document.body.append(template);

  const modalEl = document.getElementById(id);
  const modal = new A11yDialog(modalEl);

  modal.on("show", () => {
    document.documentElement.classList.add("has-modal-active");

    activeModal = modal;
  });

  modal.on("hide", () => {
    document.documentElement.classList.remove("has-modal-active");
    document.querySelector("[data-modal-wrapper]").remove();

    activeModal = null;
  });

  modal.show();

  populateModal(
    {
      type: type,
      url: opts.url ?? false,
    },
    modalEl,
  );
};

const handleModal = (e) => {
  e.preventDefault();

  const triggerEl = e.target.closest("[data-modal]");

  openModal({
    type: triggerEl.dataset.modal,
    title: triggerEl.dataset.modalTitle,
    url: triggerEl.href,
    allowOverlayClose: triggerEl.dataset.modalDisableOverlayClose === undefined,
  });
};

const handleModalClose = () => {
  if (activeModal) {
    activeModal.hide();
  }
};

const handleModalReplace = async (e) => {
  e.preventDefault();

  const triggerEl = e.target.closest("[data-modal-replace]");
  const url = triggerEl.href;
  const currentModal = document.querySelector("[data-modal-wrapper]");
  const modalContent = await ajax.get(url).text();

  currentModal.querySelector("[data-modal-container]").innerHTML = modalContent;
};

const handleModalOpen = (e) => {
  openModal(e.detail);
};

export default {
  init: () => {
    delegate("[data-modal]", "click", handleModal);
    delegate("[data-modal-replace]", "click", handleModalReplace);
    delegate("html", "modal:open", handleModalOpen);
    delegate("html", "modal:close", handleModalClose);

    // If we have video modals, load the YouTube API
    if (document.querySelectorAll('[data-modal="video"]').length) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";

      const firstScriptTag = document.getElementsByTagName("script")[0];

      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }
  },
};
